'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, FileText, Clock, Pill, Snowflake, Coffee, Utensils, ArrowLeft, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

export default function SurgeryCareSection() {
    const { t } = useTranslation('dental_implants');
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const sectionRef = useRef<HTMLElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    const beforeCare = [
        {
            icon: <FileText className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.before_care.item_1.title')),
            description: localizeText(t('surgery_care.before_care.item_1.description')),
            status: t('surgery_care.before_care.item_1.status')
        },
        {
            icon: <FileText className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.before_care.item_2.title')),
            description: localizeText(t('surgery_care.before_care.item_2.description')),
            status: t('surgery_care.before_care.item_2.status')
        },
        {
            icon: <CheckCircle className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.before_care.item_3.title')),
            description: localizeText(t('surgery_care.before_care.item_3.description')),
            status: t('surgery_care.before_care.item_3.status')
        },
        {
            icon: <Coffee className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.before_care.item_4.title')),
            description: localizeText(t('surgery_care.before_care.item_4.description')),
            status: t('surgery_care.before_care.item_4.status')
        }
    ];

    const afterCare = [
        {
            icon: <Clock className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.after_care.item_1.title')),
            description: localizeText(t('surgery_care.after_care.item_1.description')),
            status: t('surgery_care.after_care.item_1.status')
        },
        {
            icon: <Snowflake className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.after_care.item_2.title')),
            description: localizeText(t('surgery_care.after_care.item_2.description')),
            status: t('surgery_care.after_care.item_2.status')
        },
        {
            icon: <CheckCircle className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.after_care.item_3.title')),
            description: localizeText(t('surgery_care.after_care.item_3.description')),
            status: t('surgery_care.after_care.item_3.status')
        },
        {
            icon: <Pill className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.after_care.item_4.title')),
            description: localizeText(t('surgery_care.after_care.item_4.description')),
            status: t('surgery_care.after_care.item_4.status')
        },
        {
            icon: <Utensils className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.after_care.item_5.title')),
            description: localizeText(t('surgery_care.after_care.item_5.description')),
            status: t('surgery_care.after_care.item_5.status')
        },
        {
            icon: <FileText className="w-6 h-6 text-gold" />,
            title: localizeText(t('surgery_care.after_care.item_6.title')),
            description: localizeText(t('surgery_care.after_care.item_6.description')),
            status: t('surgery_care.after_care.item_6.status')
        }
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            if (cardsRef.current?.children) {
                Array.from(cardsRef.current.children).forEach((card, index) => {
                    gsap.fromTo(card,
                        { opacity: 0, y: 30, scale: 0.95 },
                        {
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            duration: 0.6,
                            delay: index * 0.1,
                            ease: "power3.out",
                            scrollTrigger: {
                                trigger: cardsRef.current,
                                start: "top 80%",
                                toggleActions: "play none none reverse"
                            }
                        }
                    );
                });
            }

            // Image animation
            if (imageRef.current) {
                gsap.fromTo(imageRef.current,
                    { opacity: 0, y: 40 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: imageRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'critical': return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20';
            case 'important': return 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20';
            case 'required': return 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20';
            case 'conditional': return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20';
            default: return 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'critical': return <CheckCircle className="w-5 h-5 text-red-600" />;
            case 'important': return <CheckCircle className="w-5 h-5 text-yellow-600" />;
            case 'required': return <CheckCircle className="w-5 h-5 text-blue-600" />;
            case 'conditional': return <CheckCircle className="w-5 h-5 text-gray-600" />;
            default: return <CheckCircle className="w-5 h-5 text-gold" />;
        }
    };

    return (
        <section
            id="care"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className={cn(
                        "text-4xl md:text-5xl font-bold text-navy dark:text-white mb-6 font-serif",
                        isRTL && "font-arabic"
                    )}>
                        {localizeText(t('surgery_care.title_part1'))}{' '}
                        <span className="text-gold">{localizeText(t('surgery_care.title_part2'))}</span>
                    </h2>
                    <p className={cn(
                        "text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed",
                        isRTL && "font-arabic"
                    )}>
                        {localizeText(t('surgery_care.description'))}
                    </p>
                </div>

                {/* Dental Implant Visualization */}
                <div className="mb-16">
                    <div ref={imageRef} className="flex justify-center">
                        <div className="relative max-w-2xl w-full">
                            <Image
                                src="/images/treatments/implant/dental-implant-vis2.webp"
                                alt={t('surgery_care.image_alt')}
                                width={600}
                                height={400}
                                className="w-full h-auto rounded-2xl shadow-xl"
                                priority
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl pointer-events-none"></div>
                        </div>
                    </div>
                </div>

                <div ref={cardsRef} className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
                    {/* Before Surgery */}
                    <div className="space-y-6">
                        <div className={cn(
                            "text-center mb-8",
                            isRTL ? "lg:text-right" : "lg:text-left"
                        )}>
                            <h3 className={cn(
                                "text-2xl font-bold text-navy dark:text-white mb-4",
                                isRTL && "font-arabic"
                            )}>
                                {localizeText(t('surgery_care.before_section.title'))}
                            </h3>
                            <p className={cn(
                                "text-slate-600 dark:text-slate-300",
                                isRTL && "font-arabic"
                            )}>
                                {localizeText(t('surgery_care.before_section.description'))}
                            </p>
                        </div>

                        <div className="space-y-4">
                            {beforeCare.map((item, index) => (
                                <div
                                    key={index}
                                    className={`p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-md ${getStatusColor(item.status)}`}
                                >
                                    <div className={cn(
                                        "flex items-start space-x-4",
                                        isRTL && "space-x-reverse"
                                    )}>
                                        <div className="shrink-0 p-2 rounded-lg bg-white dark:bg-slate-800">
                                            {item.icon}
                                        </div>
                                        <div className="flex-1">
                                            <div className={cn(
                                                "flex items-center space-x-3 mb-2",
                                                isRTL && "space-x-reverse"
                                            )}>
                                                {getStatusIcon(item.status)}
                                                <h4 className={cn(
                                                    "font-semibold text-navy dark:text-white",
                                                    isRTL && "font-arabic"
                                                )}>
                                                    {item.title}
                                                </h4>
                                            </div>
                                            <p className={cn(
                                                "text-slate-600 dark:text-slate-300 text-sm",
                                                isRTL && "font-arabic"
                                            )}>
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* After Surgery */}
                    <div className="space-y-6">
                        <div className={cn(
                            "text-center mb-8",
                            isRTL ? "lg:text-right" : "lg:text-left"
                        )}>
                            <h3 className={cn(
                                "text-2xl font-bold text-navy dark:text-white mb-4",
                                isRTL && "font-arabic"
                            )}>
                                {localizeText(t('surgery_care.after_section.title'))}
                            </h3>
                            <p className={cn(
                                "text-slate-600 dark:text-slate-300",
                                isRTL && "font-arabic"
                            )}>
                                {localizeText(t('surgery_care.after_section.description'))}
                            </p>
                        </div>

                        <div className="space-y-4">
                            {afterCare.map((item, index) => (
                                <div
                                    key={index}
                                    className={`p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-md ${getStatusColor(item.status)}`}
                                >
                                    <div className={cn(
                                        "flex items-start space-x-4",
                                        isRTL && "space-x-reverse"
                                    )}>
                                        <div className="shrink-0 p-2 rounded-lg bg-white dark:bg-slate-800">
                                            {item.icon}
                                        </div>
                                        <div className="flex-1">
                                            <div className={cn(
                                                "flex items-center space-x-3 mb-2",
                                                isRTL && "space-x-reverse"
                                            )}>
                                                {getStatusIcon(item.status)}
                                                <h4 className={cn(
                                                    "font-semibold text-navy dark:text-white",
                                                    isRTL && "font-arabic"
                                                )}>
                                                    {item.title}
                                                </h4>
                                            </div>
                                            <p className={cn(
                                                "text-slate-600 dark:text-slate-300 text-sm",
                                                isRTL && "font-arabic"
                                            )}>
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Status Legend */}
                <div className="mt-12 bg-slate-50 dark:bg-navy-950 rounded-2xl p-8">
                    <h4 className={cn(
                        "text-lg font-semibold text-navy dark:text-white mb-6 text-center",
                        isRTL && "font-arabic"
                    )}>
                        {localizeText(t('surgery_care.priority_levels.title'))}
                    </h4>
                    <div className="grid md:grid-cols-4 gap-4">
                        <div className={cn(
                            "flex items-center space-x-3",
                            isRTL && "space-x-reverse"
                        )}>
                            <CheckCircle className="w-5 h-5 text-red-600" />
                            <span className={cn(
                                "text-sm text-slate-600 dark:text-slate-300",
                                isRTL && "font-arabic"
                            )}>{localizeText(t('surgery_care.priority_levels.critical'))}</span>
                        </div>
                        <div className={cn(
                            "flex items-center space-x-3",
                            isRTL && "space-x-reverse"
                        )}>
                            <CheckCircle className="w-5 h-5 text-yellow-600" />
                            <span className={cn(
                                "text-sm text-slate-600 dark:text-slate-300",
                                isRTL && "font-arabic"
                            )}>{localizeText(t('surgery_care.priority_levels.important'))}</span>
                        </div>
                        <div className={cn(
                            "flex items-center space-x-3",
                            isRTL && "space-x-reverse"
                        )}>
                            <CheckCircle className="w-5 h-5 text-blue-600" />
                            <span className={cn(
                                "text-sm text-slate-600 dark:text-slate-300",
                                isRTL && "font-arabic"
                            )}>{localizeText(t('surgery_care.priority_levels.required'))}</span>
                        </div>
                        <div className={cn(
                            "flex items-center space-x-3",
                            isRTL && "space-x-reverse"
                        )}>
                            <CheckCircle className="w-5 h-5 text-gray-600" />
                            <span className={cn(
                                "text-sm text-slate-600 dark:text-slate-300",
                                isRTL && "font-arabic"
                            )}>{localizeText(t('surgery_care.priority_levels.conditional'))}</span>
                        </div>
                    </div>
                </div>

                {/* Download PDF */}
                <div className="text-center mt-12">
                    <Button
                        size="lg"
                        variant="outline"
                        className={cn(
                            "border-2 border-gold text-gold hover:bg-gold hover:text-navy px-8 py-4 rounded-xl transition-all duration-300",
                            isRTL && "font-arabic"
                        )}
                    >
                        {isRTL ? (
                            <>
                                {localizeText(t('surgery_care.download_guide_button'))}
                                <FileText className="w-5 h-5 ml-2" />
                            </>
                        ) : (
                            <>
                                <FileText className="w-5 h-5 mr-2" />
                                {localizeText(t('surgery_care.download_guide_button'))}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </section>
    );
} 
